import { launchSchema } from '@mio/shared';
import { ArrowUpRight } from 'lucide-react';
import { useState } from 'react';

import { apiRequest } from '@/shared/api/client';
import { Button } from '@/shared/ui/button';

import type { CurrentWorkspace } from '@mio/shared';

export function WorkshopPreview({
  workspace,
  disabled,
}: {
  workspace: NonNullable<CurrentWorkspace['workspace']>;
  disabled: boolean;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  async function open() {
    // Reserve the browsing context in the click event, before the async grant.
    const name = `mio-preview-${workspace.id}`;
    const tab = window.open('about:blank', name);
    if (!tab) {
      setError('Allow this app to open a preview tab, then try again.');
      return;
    }
    tab.opener = null;
    setPending(true);
    setError('');
    try {
      const launch = await apiRequest(
        `/workshop/attempts/${workspace.activeAttemptId}/preview`,
        launchSchema,
        {
          method: 'POST',
          body: {
            expectedGeneration: workspace.generation,
            expectedAttemptId: workspace.activeAttemptId,
          },
        },
      );
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = launch.action;
      form.target = name;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'grant';
      input.value = launch.grant;
      form.append(input);
      document.body.append(form);
      form.submit();
      form.remove();
    } catch (cause) {
      tab.close();
      setError(cause instanceof Error ? cause.message : 'Could not open the app.');
    } finally {
      setPending(false);
    }
  }
  return (
    <div className="relative shrink-0">
      <Button
        size="sm"
        variant="secondary"
        disabled={disabled}
        loading={pending}
        onClick={() => void open()}
        title="Open your app in a new tab. First start its server on port 8000 using the workshop instructions."
      >
        Open app
        <ArrowUpRight className="size-3.5" aria-hidden="true" />
      </Button>
      {error ? (
        <p
          role="alert"
          className="border-line bg-canvas text-danger absolute right-0 top-full z-30 mt-2 w-64 rounded-lg border p-3 text-xs shadow-lg"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
